import React, { useContext } from 'react';
import {
  Box,
  Stack,
  Typography,
  styled,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import SecurityIcon from '@mui/icons-material/Security';
import { useIntl } from 'react-intl';
import translations from 'utils/translations';
import { Controller } from 'react-hook-form';
import CustomRadioButtonV5 from 'components/JsonApi/components/styledComponents/CustomRadioButtonV5';
import { useTicketsStore } from 'pages/ticketV2/store/ticketsStore';
import { CartContext } from 'context/CartContextProvider';

const StyledRadio = styled(CustomRadioButtonV5)(() => ({
  '& legend': {
    borderBottom: 'none',
  },
  '& .MuiFormControlLabel-label': {
    fontSize: '16px',
  },
  '& .MuiTypography-body1 .MuiBox-root': {
    color: '#333333 !important',
    fontFamily: 'Suisse-Intl',
  },
  '& .MuiFormControlLabel-label .MuiTypography-body1': {
    color: '#333333 !important',
    fontFamily: 'Suisse-Intl',
    fontSize: '16px',
  },
  '& .MuiTypography-s': {
    color: '#333333 !important',
    fontFamily: 'Suisse-Intl',
    fontSize: '16px',
  },
}));

const ContainerConsent = ({ form, locale }) => {
  // Hooks
  const intl = useIntl();
  const i18n = translations.Tickets(intl).edit.ticketEditCard;
  const i18nV2 = translations.TicketsV2(intl);
  const { customer } = useContext(CartContext);
  const theme = useTheme();
  const isLgUp = useMediaQuery(theme.breakpoints.up('lg'));

  const { errors, control } = form;

  const consentOptions = [
    { value: '0', label: i18nV2.Common.no },
    { value: '1', label: i18nV2.Common.yes },
  ];

  const [loggedDataAtIndex, stepperCurrentTicketIdx] = useTicketsStore(
    (state) => [
      state.loggedDataAtIndex,
      state.stepperCurrentTicketIdx,
      state.ticketsData,
    ]
  );

  // Renders
  const renderError = (key, labelKey, marginTop = 0) => {
    if (errors[key] && errors[key]?.type === 'required')
      return (
        <Typography
          style={{
            marginTop: marginTop,
          }}
          variant={'body2'}
          color="primary"
          fontFamily={'Suisse-Intl'}
        >
          {i18n.fields[labelKey ? labelKey : key].error}
        </Typography>
      );
    return null;
  };

  const renderErrorCustom = (key, labelKey, marginTop = 0) => {
    if (errors[key]) {
      let errorMessage;

      if (errors[key]?.type === 'required')
        errorMessage = i18n.fields[labelKey ? labelKey : key].error;

      if (
        errors[key]?.type === 'custom' ||
        errors[key]?.type === 'matchPattern'
      )
        errorMessage = errors[key]?.message;

      return (
        <Typography
          style={{
            marginTop: marginTop,
          }}
          variant={'body2'}
          color="primary"
          fontFamily={'Suisse-Intl'}
        >
          {errorMessage}
        </Typography>
      );
    }
    return null;
  };

  if (loggedDataAtIndex === stepperCurrentTicketIdx && customer?.accountid) {
    form.setValue('owner_flag_privacy_1', '1');
    form.setValue('owner_flag_privacy_2', customer?.flagMarketing ? '1' : '0');
    form.setValue(
      'owner_flag_privacy_3',
      customer?.flagProfilazione ? '1' : '0'
    );
    return <></>;
  }

  return (
    <Box
      sx={{
        width: '100%',
      }}
    >
      <Stack
        gap={'25px'}
        sx={{
          p: '30px',
          boxShadow: '0px 0px 5px 0px rgba(51, 51, 51, 0.05)',
          bgcolor: 'white',
        }}
      >
        <Stack direction={'row'} alignItems={'center'} gap={'10px'}>
          {isLgUp ? (
            <div style={{ fontSize: '27px', paddingTop: '4px' }}>
              <SecurityIcon fontSize="inherit" />
            </div>
          ) : (
            <SecurityIcon fontSize="medium" sx={{ mb: '4px' }} />
          )}
          <Typography
            variant="m-bold"
            fontFamily={'Suisse-Intl'}
            component={'h2'}
          >
            {i18nV2.Assign.consentTitle}
          </Typography>
        </Stack>
        <div>
          <Controller
            name={'owner_flag_privacy_1'}
            control={control}
            rules={{ required: true }}
            defaultValue={'3'}
            render={(props) => {
              return (
                <StyledRadio
                  isRequired={false}
                  description={
                    <>
                      {i18nV2.Assign.flags.privacyOne}{' '}
                      <a
                        style={{
                          fontWeight: 'bold',
                          textDecoration: 'underline !important',
                          color: '#000',
                        }}
                        target="_blank"
                        href={`https://www.salonemilano.it/${
                          locale === 'it' ? 'it' : 'en'
                        }/privacy-policy`}
                        rel="noreferrer"
                      >
                        https://www.salonemilano.it/
                        {locale === 'it' ? 'it' : 'en'}/privacy-policy.*
                      </a>
                    </>
                  }
                  options={consentOptions}
                  value={props.value}
                  onChange={(e) => {
                    props.onChange(e.target.value);
                  }}
                  sx={{ fontFamily: 'Suisse-Intl !important' }}
                  hasCustomError={
                    errors['owner_flag_privacy_1'] &&
                    errors['owner_flag_privacy_1']?.type === 'custom'
                  }
                />
              );
            }}
          />
          {renderErrorCustom('owner_flag_privacy_1')}
          {renderError('owner_flag_privacy_1')}
        </div>
        <div>
          <Controller
            name={'owner_flag_privacy_2'}
            control={control}
            defaultValue={'3'}
            render={(props) => {
              return (
                <StyledRadio
                  isRequired={false}
                  description={i18nV2.Assign.flags.privacyTwo}
                  options={consentOptions}
                  value={props.value}
                  onChange={(e) => {
                    props.onChange(e.target.value);
                  }}
                  hasCustomError={
                    errors['owner_flag_privacy_2'] &&
                    errors['owner_flag_privacy_2']?.type === 'custom'
                  }
                />
              );
            }}
          />
          {renderErrorCustom('owner_flag_privacy_2')}
        </div>
        <div>
          <Controller
            name={'owner_flag_privacy_3'}
            control={control}
            defaultValue={'3'}
            render={(props) => {
              return (
                <StyledRadio
                  isRequired={false}
                  description={i18nV2.Assign.flags.privacyThree}
                  options={consentOptions}
                  value={props.value}
                  onChange={(e) => {
                    props.onChange(e.target.value);
                  }}
                  hasCustomError={
                    errors['owner_flag_privacy_3'] &&
                    errors['owner_flag_privacy_3']?.type === 'custom'
                  }
                />
              );
            }}
          />
          {renderErrorCustom('owner_flag_privacy_3')}
        </div>
      </Stack>
    </Box>
  );
};
export default ContainerConsent;
